import React, { useEffect, useRef, useState, useCallback } from "react";
import { api, arabicError } from "../lib/api";
import { toast } from "sonner";
import { X, Send } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { logger } from "../lib/logger";

/**
 * Modal thread view for a story-reply direct-message conversation.
 * Opens when the user clicks a thread in the DirectMessagesInbox list.
 */
const DirectMessageThread = ({ storyId, otherUserId, otherUserName, storyCaption, onClose, onSent }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState("");
  const [dest, setDest] = useState("both");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/direct-messages/thread/${storyId}/${otherUserId}`);
      setMessages(r.data);
    } catch (e) { toast.error(arabicError(e)); }
    finally { setLoading(false); }
  }, [storyId, otherUserId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (e) => {
    e?.preventDefault();
    if (!input.trim()) return;
    setSending(true);
    try {
      const r = await api.post(`/direct-messages/thread/${storyId}/${otherUserId}`, { message: input.trim(), destination: dest });
      setMessages((prev) => [...prev, r.data]);
      setInput("");
      onSent?.();
    } catch (e) { toast.error(arabicError(e)); }
    finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-3" onClick={onClose} dir="rtl">
      <div className="bg-white rounded-2xl w-full max-w-2xl h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="dm-thread-modal">
        <div className="p-4 border-b border-[#EBF4F6] flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <div className="font-extrabold text-[#333333] truncate">{otherUserName}</div>
            <div className="text-xs text-[#666666] truncate">رد على ستوري{storyCaption ? ` · ${storyCaption}` : ""}</div>
          </div>
          <button onClick={onClose} className="w-10 h-10 rounded-lg hover:bg-[#EBF4F6] flex items-center justify-center"><X /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="dm-messages">
          {loading ? (
            <div className="text-center text-[#666666] py-8">جارٍ التحميل...</div>
          ) : messages.length === 0 ? (
            <div className="text-center text-[#666666] py-10">لا توجد رسائل بعد</div>
          ) : messages.map((m) => {
            const mine = m.sender_id === user.id;
            return (
              <div key={m.id} className={`flex ${mine ? "justify-start" : "justify-end"}`}>
                <div className={`max-w-[75%] rounded-2xl p-3 ${mine ? "bg-[#09637E] text-white" : "bg-[#EBF4F6] text-[#333333]"}`}>
                  <div className="text-xs font-bold opacity-80 mb-1">{m.sender_name}</div>
                  <div className="whitespace-pre-wrap break-words">{m.message}</div>
                  <div className="text-[10px] opacity-70 mt-1">{new Date(m.ts).toLocaleTimeString("ar-SY", { hour: "2-digit", minute: "2-digit" })}</div>
                </div>
              </div>
            );
          })}
          <div ref={endRef} />
        </div>
        <form onSubmit={send} className="border-t border-[#7AB2B2] p-3 flex flex-wrap sm:flex-nowrap gap-2">
          <select value={dest} onChange={(e) => setDest(e.target.value)} data-testid="dm-destination-select"
            title="وجهة إشعار الرسالة"
            className="h-11 w-full sm:w-auto shrink-0 rounded-lg border border-[#7AB2B2] bg-white px-2 text-xs font-bold text-[#09637E] focus:outline-none focus:ring-2 focus:ring-[#09637E]">
            <option value="both">التطبيق والبريد</option>
            <option value="app">التطبيق فقط</option>
            <option value="email">البريد فقط</option>
          </select>
          <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="اكتب ردك..." data-testid="dm-input"
            className="flex-1 min-w-0 h-11 rounded-lg border border-[#7AB2B2] px-4 focus:outline-none focus:ring-2 focus:ring-[#09637E]" />
          <button type="submit" disabled={sending || !input.trim()} data-testid="dm-send"
            className="h-11 px-5 shrink-0 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white font-bold flex items-center gap-1 disabled:opacity-60">
            <Send size={18} /> إرسال
          </button>
        </form>
      </div>
    </div>
  );
};

/**
 * Inbox list of story-reply threads. Rendered at the top of the Messages page.
 * Auto-refreshes when the parent triggers a reload or when a WS notification of
 * type `story_reply` / `message_new` arrives.
 */
const DirectMessagesInbox = ({ reloadSignal = 0 }) => {
  const [threads, setThreads] = useState([]);
  const [openThread, setOpenThread] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await api.get("/direct-messages/threads");
      setThreads(r.data);
    } catch (err) { logger.warn("dm threads fetch failed:", err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [reloadSignal]);

  if (!loading && threads.length === 0) return null;

  return (
    <div className="bg-white border border-[#7AB2B2] rounded-xl overflow-hidden" data-testid="direct-messages-inbox">
      <div className="p-3 bg-[#EBF4F6] border-b border-[#7AB2B2] font-extrabold text-[#333333] text-sm">
        الردود على القصص ({threads.length})
      </div>
      {loading ? (
        <div className="p-6 text-center text-[#666666] text-sm">جارٍ التحميل...</div>
      ) : (
        <ul className="divide-y divide-[#EBF4F6]" data-testid="dm-threads-list">
          {threads.map((t) => (
            <li key={`${t.story_id}-${t.other_user_id}`}>
              <button
                onClick={() => setOpenThread(t)}
                data-testid={`dm-thread-${t.story_id}-${t.other_user_id}`}
                className="w-full text-right p-3 hover:bg-[#EBF4F6] flex items-start gap-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-bold text-[#333333] truncate">{t.other_user_name}</span>
                    <span className="text-xs text-[#666666] shrink-0">
                      {new Date(t.last_ts).toLocaleTimeString("ar-SY", { hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </div>
                  <div className="text-xs text-[#666666] mt-0.5 truncate">
                    {t.last_sender_is_me ? "أنت: " : ""}{t.last_message}
                  </div>
                  {t.story_caption && (
                    <div className="text-[10px] text-[#09637E] mt-0.5 truncate">ستوري: {t.story_caption}</div>
                  )}
                </div>
                {t.unread > 0 && (
                  <span className="min-w-[22px] h-[22px] px-1.5 rounded-full bg-red-500 text-white text-[11px] font-extrabold flex items-center justify-center shrink-0">
                    {t.unread > 9 ? "+9" : t.unread}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}

      {openThread && (
        <DirectMessageThread
          storyId={openThread.story_id}
          otherUserId={openThread.other_user_id}
          otherUserName={openThread.other_user_name}
          storyCaption={openThread.story_caption}
          onClose={() => { setOpenThread(null); load(); }}
          onSent={load}
        />
      )}
    </div>
  );
};

export default DirectMessagesInbox;
